import { FC, useCallback } from 'react';
import {
    Badge,
    ButtonSizes,
    ButtonStyles,
    EntityChip,
    Icons,
    List,
    ListItem,
    MenuButton,
    showAlert
} from '@silverfort/sf-components';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { User, UsersBoundLocalAccount } from '../../../types/User';
import { isAccountMatchingEnabled, isMfaModuleEnabled } from '../../../utils';
import { USER_STATUS_TO_LABEL } from '../../../consts';
import { useRemoveUserPopup } from '../hooks/useRemoveUserPopup';
import { useSnackAlert } from '../../../context/SnackAlertProvider';
import { useExcludeUserPopup } from '../hooks/useExcludeUserPopup';
import { useUpdateUser } from '../../../api';
import { useUsers } from '../../../context/UserManagementContext';
import { usePricingModulesState } from '../../../../../context/contexts/PricingModuleContext';
import { useUnmatchUsersPopup } from '../hooks/useUnmatchUsersPopup';
import { isUserPermitted } from '../../../../../TowerMenu/TowerMenuUtils';
import { UserSession } from '../../../../../api';
import { useBoundLocalAccountsPopup } from '../hooks/useBoundLocalAccountsPopup';

export interface ContentHeaderProps {
    user: User;
    secondaryUsers: User[];
    boundLocalAccounts: UsersBoundLocalAccount[];
    boundLocalAccountsCount: number;
    onUserUpdated: () => void;
    userSession: UserSession;
}

const ContentHeader: FC<ContentHeaderProps> = ({
    user,
    userSession,
    onUserUpdated,
    secondaryUsers,
    boundLocalAccounts,
    boundLocalAccountsCount
}) => {
    const { systemData } = useUsers();

    const { pricingModulesState } = usePricingModulesState();
    const { openSnackAlert, closeSnackAlert } = useSnackAlert();
    const userStatus = user.status;
    const hasAccountMatching = isAccountMatchingEnabled(systemData);
    const mfaModuleEnabled = isMfaModuleEnabled(pricingModulesState);
    const hasSecondaryUsers = secondaryUsers.length > 0;
    const hasBoundLocalAccounts = boundLocalAccounts.length > 0;

    const { openUnmatchUsersPopup } = useUnmatchUsersPopup({
        onUnmatchedUsers: onUserUpdated
    });

    const handleUserRemoved = useCallback(() => {
        openSnackAlert({
            message: `${user.displayName} was removed`,
            iconLeft: Icons.Block,
            onClose: closeSnackAlert
        });

        onUserUpdated();
    }, [onUserUpdated, openSnackAlert, user.displayName, closeSnackAlert]);

    const { openRemoveUserPopup } = useRemoveUserPopup({ user, onUserRemoved: handleUserRemoved });

    const handleUserExcluded = useCallback(() => {
        openSnackAlert({
            message: `${user.displayName} was excluded`,
            iconLeft: Icons.Block,
            onClose: closeSnackAlert
        });

        onUserUpdated();
    }, [onUserUpdated, openSnackAlert, user.displayName, closeSnackAlert]);

    const { openExcludeUserPopup } = useExcludeUserPopup({ user, onUserExcluded: handleUserExcluded });

    const handleUserExcludeCanceled = useCallback(() => {
        openSnackAlert({
            message: `Protection resumed for ${user.displayName}`,
            onClose: closeSnackAlert
        });

        onUserUpdated();
    }, [onUserUpdated, openSnackAlert, user.displayName, closeSnackAlert]);

    const handleUserExcludeCancelError = useCallback(() => {
        showAlert('Failed to resume protection, please try again', 'error');
    }, []);

    const { updateUser } = useUpdateUser({
        onSuccess: handleUserExcludeCanceled,
        onError: handleUserExcludeCancelError
    });

    const handleResumeProtection = () => {
        updateUser(user.guid, { excludeTill: null });
    };

    const { openBoundLocalAccountsPopup } = useBoundLocalAccountsPopup({
        localAccounts: boundLocalAccounts,
        localAccountsCount: boundLocalAccountsCount
    });

    return (
        <div className="border-b border-sf-gray-100">
            <div className="h-14 px-6 py-2 flex justify-between items-center">
                <div className="flex gap-2 items-center">
                    <span className="font-sf font-medium text-14 leading-[17.09px]">
                        {hasAccountMatching ? user.displayName : user.userName}
                    </span>
                    <Badge className="font-medium" text={USER_STATUS_TO_LABEL[userStatus]} />
                </div>
                <MenuButton
                    className="left-[-120px]"
                    iconClassName="text-sf-gray-400"
                    buttonStyle={ButtonStyles.Tertiary}
                    buttonSize={ButtonSizes.Medium}
                    openByDefault={false}
                    content={
                        <List>
                            {mfaModuleEnabled && !user.excludeTill && (
                                <ListItem
                                    title="Temporarily exclude"
                                    onClick={openExcludeUserPopup}
                                    disabled={!isUserPermitted(userSession, 'excludeUsers', ROLES.EDITOR.value)}
                                />
                            )}
                            {mfaModuleEnabled && user.excludeTill && (
                                <ListItem
                                    title="Resume protection"
                                    onClick={handleResumeProtection}
                                    disabled={!isUserPermitted(userSession, 'excludeUsers', ROLES.EDITOR.value)}
                                />
                            )}
                            {hasAccountMatching && secondaryUsers.length > 0 && (
                                <ListItem
                                    title="Unmatch"
                                    onClick={() => openUnmatchUsersPopup(secondaryUsers)}
                                    disabled={!isUserPermitted(userSession, 'matchUsers', ROLES.EDITOR.value)}
                                />
                            )}
                            <ListItem
                                title="Remove"
                                onClick={openRemoveUserPopup}
                                disabled={!isUserPermitted(userSession, 'manageProtectedGroups', ROLES.EDITOR.value)}
                            />
                        </List>
                    }
                />
            </div>
            {(hasAccountMatching || hasBoundLocalAccounts) && (
                <div className="px-6 pb-4 flex gap-8">
                    <div className="flex flex-col gap-2 justify-center">
                        <div className="font-sf font-medium text-12 leading-[15px]">
                            {hasSecondaryUsers ? 'Primary Account' : 'AD User'}
                        </div>
                        <div>
                            <EntityChip key={user.guid} text={user.userName} icon={Icons.User} />
                        </div>
                    </div>
                    {hasSecondaryUsers && (
                        <div className="flex flex-col gap-2 justify-center">
                            <div className="font-sf font-medium text-12 leading-[15px]">Secondary Accounts</div>
                            <div>
                                {secondaryUsers.map((secondaryUser) => (
                                    <EntityChip
                                        key={secondaryUser.guid}
                                        className="leading-[14.32px]"
                                        text={secondaryUser.userName}
                                        icon={Icons.User}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                    {hasBoundLocalAccounts && (
                        <div className="flex flex-col gap-2 justify-center">
                            <div className="font-sf font-medium text-12 leading-[15px]">Local Accounts</div>
                            <div className="flex gap-2">
                                <EntityChip
                                    key={boundLocalAccounts[0].guid}
                                    text={boundLocalAccounts[0].hostname}
                                    icon={Icons.LocalAccount}
                                    className="!bg-sf-gray-50 text-13"
                                    iconClassName="!text-sf-gray-400"
                                    onClick={openBoundLocalAccountsPopup}
                                />
                                {boundLocalAccountsCount > 1 && (
                                    <EntityChip
                                        text={`+${boundLocalAccountsCount - 1}`}
                                        className="text-13"
                                        onClick={openBoundLocalAccountsPopup}
                                    />
                                )}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ContentHeader;
